import { create } from 'zustand';

/** Severity levels for alerts - critical ones trigger toast notifications */
export const ALERT_SEVERITY = {
  critical: 'critical',
  warning: 'warning',
  info: 'info',
} as const;

export type AlertSeverity = (typeof ALERT_SEVERITY)[keyof typeof ALERT_SEVERITY];

/** Categories matching dashboard data sources */
export const ALERT_CATEGORY = {
  admit: 'admit',
  discharge: 'discharge',
  allergyChange: 'allergyChange',
  dietOrderChange: 'dietOrderChange',
  trayLineWarning: 'trayLineWarning',
  unitRoomBedChange: 'unitRoomBedChange',
  mealPatternNote: 'mealPatternNote',
  selectiveMenu: 'selectiveMenu',
  bedsideDevice: 'bedsideDevice',
  bedsideHelp: 'bedsideHelp',
  notOrdered: 'notOrdered',
  pdpmGap: 'pdpmGap',
  /**
   * An EHR message that arrived but did not land: unmatched, unmapped, failed
   * to process, or simply sitting unprocessed for too long.
   *
   * This is the only alert category that reports the SYSTEM failing rather
   * than a task being outstanding, and it is the only one whose problem is
   * invisible everywhere else in the app. A tray line warning shows on the
   * Residents grid; a weight alert shows on the resident record. A diet order
   * that never arrived shows nowhere at all — the resident keeps the diet they
   * had and every screen looks correct.
   */
  ehrMessage: 'ehrMessage',
} as const;

export type AlertCategory = (typeof ALERT_CATEGORY)[keyof typeof ALERT_CATEGORY];

export interface Alert {
  id: string;
  category: AlertCategory;
  severity: AlertSeverity;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  /** Optional link to navigate to when clicking the alert */
  link?: string;
  /** Source record key for deduplication */
  sourceKey: string;
}

interface AlertsState {
  alerts: Alert[];
  /** IDs of alerts that have already triggered a toast (prevent re-toasting on re-poll) */
  toastedIds: Set<string>;
  /** sourceKeys that the user has explicitly dismissed — survives re-polls */
  dismissedKeys: Set<string>;
  /** Timestamp of last successful poll */
  lastPollTime: number;

  /** Replace all alerts with a fresh set (from polling). Returns newly added alert IDs. */
  setAlerts: (alerts: Alert[]) => string[];
  markRead: (id: string) => void;
  markAllRead: () => void;
  /** Dismiss an alert by id. Pass persistKey=true to also suppress it across re-polls. */
  dismissAlert: (id: string, persistKey?: boolean) => void;
  clearAll: () => void;
  markToasted: (ids: string[]) => void;
}

export const useAlertsStore = create<AlertsState>((set, get) => ({
  alerts: [],
  toastedIds: new Set(),
  dismissedKeys: new Set(),
  lastPollTime: 0,

  setAlerts: (incoming) => {
    const existing = get().alerts;
    const dismissedKeys = get().dismissedKeys;
    const existingKeys = new Set(existing.map((a) => a.sourceKey));
    const newAlertIds: string[] = [];

    // Preserve read state for alerts that already exist
    const readMap = new Map(existing.map((a) => [a.sourceKey, a.read]));

    // Filter out alerts whose sourceKey was explicitly dismissed
    const merged = incoming
      .filter((a) => !dismissedKeys.has(a.sourceKey))
      .map((a) => {
        const wasRead = readMap.get(a.sourceKey);
        if (!existingKeys.has(a.sourceKey)) {
          newAlertIds.push(a.id);
        }
        return { ...a, read: wasRead ?? a.read };
      });

    set({ alerts: merged, lastPollTime: Date.now() });
    return newAlertIds;
  },

  markRead: (id) =>
    set((s) => ({
      alerts: s.alerts.map((a) => (a.id === id ? { ...a, read: true } : a)),
    })),

  markAllRead: () =>
    set((s) => ({
      alerts: s.alerts.map((a) => ({ ...a, read: true })),
    })),

  dismissAlert: (id, persistKey) => {
    const alert = get().alerts.find((a) => a.id === id);
    set((s) => {
      const next: Partial<AlertsState> = { alerts: s.alerts.filter((a) => a.id !== id) };
      if (persistKey && alert) {
        const keys = new Set(s.dismissedKeys);
        keys.add(alert.sourceKey);
        next.dismissedKeys = keys;
      }
      return next as AlertsState;
    });
  },

  clearAll: () => set({ alerts: [] }),

  markToasted: (ids) =>
    set((s) => {
      const next = new Set(s.toastedIds);
      ids.forEach((id) => next.add(id));
      return { toastedIds: next };
    }),
}));

/** Selector: count of unread alerts */
export const selectUnreadCount = (s: AlertsState) =>
  s.alerts.filter((a) => !a.read).length;
